import { Application, Container, Ticker } from 'pixi.js';
import { InteractionManager } from './InteractionManager';
import { ViewportManager } from './ViewportManager';
import { ShapeManager } from './ShapeManager';
import { CommandManager } from './CommandManager';
import { ToolModeManager } from './ToolModeManager';

export class Canvas {
    private _app: Application;
    private container: Container;
    private viewportManager: ViewportManager;
    private interactionManager: InteractionManager;
    private _shapeManager: ShapeManager;
    private commandManager: CommandManager;
    private toolModeManager: ToolModeManager;

    constructor(element: HTMLElement) {
        this._app = new Application();
        this.container = new Container();
        this.commandManager = new CommandManager();
        this.toolModeManager = new ToolModeManager();
        this.viewportManager = new ViewportManager(this._app, this.container);
        this._shapeManager = new ShapeManager(this.container, this.viewportManager, this.commandManager);
        this.interactionManager = new InteractionManager(this._app, this.container, this.viewportManager, this.shapeManager, this.commandManager, this.toolModeManager);
        this.init(element);
    }

    private async init(element: HTMLElement) {
        await this.app.init({
            background: '#e8e8e8',
            resizeTo: element,
            antialias: true,
            // Add these performance optimizations
            powerPreference: 'high-performance',
            preserveDrawingBuffer: false,
            hello: false, // Disable PIXI hello message
        });

        element.appendChild(this.app.canvas);
        this.app.stage.addChild(this.container);

        // Optimize the container for better performance
        this.container.sortableChildren = true;
        // Fix: use cacheAsTexture instead of cacheAsBitmap
        this.container.cacheAsTexture(false); // Keep false for interactive elements

        // Setup interaction after app was initialized
        this._shapeManager.init();
        this.interactionManager.setup();

        // Start update loop with throttling
        this._app.ticker.add((ticker: Ticker) => {
            this.update(ticker.deltaTime);
        });
    }

    public get shapeManager(): ShapeManager {
        return this._shapeManager;
    }

    public get app(): Application {
        return this._app;
    }

    public get canvasCommandManager(): CommandManager {
        return this.commandManager;
    }

    public get canvasViewportManager(): ViewportManager {
        return this.viewportManager;
    }

    public get canvasToolModeManager(): ToolModeManager {
        return this.toolModeManager;
    }

    private update(_deltaTime: number) {

    }

    public destroy(): void {
        try {
            // Clean up interaction manager first
            if (this.interactionManager) {
                this.interactionManager.cleanup();
                this.interactionManager = null as any;
            }
            
            // Clear shape manager
            if (this.shapeManager) {
                this.shapeManager.clear();
            }
            
            // Remove canvas from DOM if it exists
            if (this._app && this._app.canvas && this._app.canvas.parentNode) {
                this._app.canvas.parentNode.removeChild(this._app.canvas);
            }
            
            // Safely destroy ticker
            if (this._app && this._app.ticker) {
                try {
                    this._app.ticker.destroy();
                } catch (tickerError) {
                    console.warn('Error destroying ticker:', tickerError);
                }
            }
            
            // Destroy the PIXI app
            if (this._app) {
                try {
                    this._app.destroy(true, { children: true, texture: true });
                } catch (appError) {
                    console.warn('Error destroying PIXI app:', appError);
                }
                this._app = null as any;
            }
            
            // Clear container reference
            this.container = null as any;
            this.viewportManager = null as any;
            this.commandManager = null as any;
            
        } catch (error) {
            console.warn('Error during canvas cleanup:', error);
        }
    }
}
